// src/utils/emailVerification.ts
//
// Ověřený e-mail (bezpečnost 2, docs/bezpecnost-2.md). Pravidla Firestore
// pustí přihlášku na závod jen s email_verified v tokenu. Přihlášení
// i registrace neověřený účet odhlásí (AuthPage), přihlášená relace
// neověřeného účtu ale může zůstat ze starší verze aplikace, která po
// registraci neodhlašovala. Ověření proběhne mimo aplikaci (odkaz
// v e-mailu) – relace o něm neví, dokud nenačte stav účtu ze serveru
// (user.reload) a nevydá si nový token.

import { useCallback, useEffect, useRef, useState } from "react";
import { sendEmailVerification, type User } from "firebase/auth";
import { reportWarning } from "./reportError";

/** Kam vede odkaz z ověřovacího e-mailu (registrace i nové odeslání). */
export const VERIFY_EMAIL_SETTINGS = {
  url: "https://lks-apka.netlify.app/email-verified",
  handleCodeInApp: false,
};

/** Prodleva mezi žádostmi o nový ověřovací e-mail – brání spamování. */
export const RESEND_VERIFICATION_COOLDOWN_SECONDS = 60;

/**
 * Token s email_verified – pravidla čtou token, ne stav účtu. Token vydaný
 * před ověřením (přihlášení trvalo, e-mail se ověřil v prohlížeči) ho ještě
 * nemá – obnoví se ze serveru.
 */
export async function ensureVerifiedToken(user: User): Promise<void> {
  const token = await user.getIdTokenResult();
  if (token.claims.email_verified !== true) await user.getIdToken(true);
}

/**
 * Stav ověření e-mailu ze serveru (user.reload); ověřený účet dostane
 * i čerstvý token. Vyhodí při chybě sítě.
 */
export async function refreshEmailVerified(user: User): Promise<boolean> {
  await user.reload();
  if (!user.emailVerified) return false;
  await ensureVerifiedToken(user);
  return true;
}

/** Výsledek „Poslat ověřovací e-mail znovu“. */
export type ResendOutcome = "sent" | "already-verified" | "cooldown";

export interface EmailVerificationState {
  /** E-mail je ověřený (nepřihlášený = true, nic se neblokuje). */
  verified: boolean;
  /** E-mail účtu (do textu hlášky). */
  email: string;
  /** Běží kontrola „Už mám ověřeno“. */
  checking: boolean;
  /** Běží odeslání ověřovacího e-mailu. */
  sending: boolean;
  /** Zbývající sekundy do dalšího odeslání (0 = jde hned). */
  cooldown: number;
  /** Stav ze serveru; vrací ověřeno ano/ne, chyba sítě vyhodí. */
  recheck: () => Promise<boolean>;
  /** Nový ověřovací e-mail (už ověřený účet nic nedostane); chyba vyhodí. */
  resend: () => Promise<ResendOutcome>;
}

/**
 * Stav ověření e-mailu přihlášeného účtu. Neověřený účet se při připojení
 * jednou potichu ověří ze serveru – e-mail mohl ověřit jinde a hláška by
 * ho jinak zbytečně zdržela.
 */
export function useEmailVerification(user: User | null): EmailVerificationState {
  const [verified, setVerified] = useState<boolean>(() => !user || user.emailVerified);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Nastavuje se i při připojení: React.StrictMode (index.tsx) ve vývoji
  // efekt odpojí a znovu připojí – bez toho by zůstal false a „Už mám
  // ověřeno“ by navždy točilo.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setVerified(true);
      return undefined;
    }
    setVerified(user.emailVerified);
    if (user.emailVerified) return undefined;
    let active = true;
    refreshEmailVerified(user).then(
      (ok) => {
        if (active && ok) setVerified(true);
      },
      (err: unknown) => reportWarning("emailVerification.refresh", err, { uid: user.uid })
    );
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const recheck = useCallback(async (): Promise<boolean> => {
    if (!user) return true;
    setChecking(true);
    try {
      const ok = await refreshEmailVerified(user);
      if (mounted.current) setVerified(ok);
      return ok;
    } finally {
      if (mounted.current) setChecking(false);
    }
  }, [user]);

  const resend = useCallback(async (): Promise<ResendOutcome> => {
    if (!user) return "already-verified";
    if (cooldown > 0) return "cooldown";
    setSending(true);
    try {
      // Mezitím ověřený účet žádný další e-mail nedostane.
      if (await refreshEmailVerified(user)) {
        if (mounted.current) setVerified(true);
        return "already-verified";
      }
      await sendEmailVerification(user, VERIFY_EMAIL_SETTINGS);
      if (mounted.current) setCooldown(RESEND_VERIFICATION_COOLDOWN_SECONDS);
      return "sent";
    } finally {
      if (mounted.current) setSending(false);
    }
  }, [user, cooldown]);

  return {
    verified,
    email: user && user.email ? user.email : "",
    checking,
    sending,
    cooldown,
    recheck,
    resend,
  };
}
